/**
 * phaseDeadlineFormat.ts — shared, dependency-free viewer-local formatter
 * for the per-Season phase deadlines (season-phase-deadlines spec, task 10).
 *
 * A stored Phase_1_Close_Date / Phase_2_Due_Date is an absolute ISO-8601
 * instant (Requirement 7.x). Unlike `countdown.ts#formatAnchorDate`, which
 * takes a date-only ISO (`YYYY-MM-DD`) and renders date-only, a deadline must
 * render both the date AND the wall-clock time in the *viewer's* local
 * timezone (Requirements 7.6, 12.2, 13.5, 14.5). Every display surface — the
 * Season tab, the Admin dashboard, the contestant dashboard, and the
 * profile/application area — consumes this one helper so they all render a
 * deadline identically.
 *
 * The helper is pure and React-free (never reads external state beyond the
 * runtime's own locale/zone via `toLocaleString`), so it is unit-testable in
 * isolation.
 *
 * Requirements: 7.6, 12.2, 13.5, 14.5
 */

export interface FormatDeadlineOptions {
  /**
   * When `true`, append the viewer's local timezone (via the
   * `timeZoneName: 'short'` option, e.g. "MDT"), so a rendered deadline is
   * unambiguous about which zone it is shown in.
   */
  withZone?: boolean;
}

/**
 * Render a stored absolute ISO-8601 instant as the wall-clock date and time in
 * the viewer's local timezone, e.g. "June 20, 2026, 5:00 PM" (or
 * "June 20, 2026, 5:00 PM MDT" when `withZone` is set).
 *
 * Returns `''` for a `null` / `undefined` / empty / whitespace-only /
 * unparseable input — an absent deadline renders nothing, so callers can emit
 * the result directly and omit the surrounding label when it is empty.
 */
export function formatDeadlineLocal(
  instant: string | null | undefined,
  opts: FormatDeadlineOptions = {},
): string {
  if (instant == null) return '';
  const trimmed = instant.trim();
  if (!trimmed) return '';

  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return '';

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  };
  if (opts.withZone) {
    options.timeZoneName = 'short';
  }

  return d.toLocaleString('en-US', options);
}
